import {Box, Divider, Flex, Icon, IconWithText, P} from 'exsportia-components'
import {TextType} from 'exsportia-components/dist/types'
import {useFormikContext} from 'formik'
import {DateTime} from 'luxon'
import React, {FC, useCallback, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import {useParams} from 'react-router-dom'

import {BookingQueries} from '../../api/booking'
import {
  CalculateBookingPriceQueryParams,
  CalculatedBookingPriceObject,
  LimitationCheckResultType,
  LimitationCheckResultTypeKey,
} from '../../api/booking/calculate-booking-price'
import {getDuration} from '../../api/booking/helpers'
import {BookingUsageDataType, Participant} from '../../api/booking/types'
import {VenueQueries} from '../../api/venue'
import {InstructorCard} from '../../components/instructor-card/instructor-card'
import {AddonQuoteProblem} from '../../components/addon-quote-problem/addon-quote-problem'
import {PlayspaceCard} from '../../components/playspace-card/playspace-card'
import {PriceRow} from '../../components/price-row/price-row'
import {PricingDetails, PricingElementType} from '../../components/pricing-details/pricing-details'
import {PromoCodeField} from '../../components/promo-code-field/promo-code-field'
import {SubscriptionCard} from '../../components/subscription-card/subscription-card'
import {ParticipantsList} from '../../components/timeslot-grid/components/participants-list'
import {usePublicCancelPolicy} from '../../api/venue/get-public-cancel-policy'
import {VenueCancelPolicyInfo} from '../../components/venue-cancel-policy-info/venue-cancel-policy-info'
import {FULL_CANCEL_POLICY_OPTIONS_MAP} from '../../constants/cancel-policy'
import {USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP} from '../../constants/usage-type'
import {getParticipantsEquipment} from '../../helpers/data'
import {CreateServiceBookingFormType} from '../../services/booking/forms/create-service-booking-form'
import {useBookingLoader} from '../../services/booking/hooks'
import {useServiceOptions} from '../../services/service-options/hooks'
import {theme} from '../../theme/theme'
import {Chip} from '../../ui/components/chip/chip'
import {CheckBox} from '../../ui/components/input/checkbox'
import {TextArea} from '../../ui/components/input/text-area'
import {LabeledValueRow} from '../../ui/components/key-value-row/labeled-value-row'
import {LabeledSection} from '../../ui/components/labeled-section/labeled-section'
import {TextWithShowMore} from '../../ui/components/text-with-show-more/text-with-show-more'
import {TitledSection} from '../../ui/components/titeled-section/titled-section'
import {auth} from '../../utils/firebase'
import {LocaleOptions, useLocale, useLuxon} from '../../utils/i18n'
import {isNilOrEmpty, isNotNilAndNotEmpty} from '../../utils/logic'
import {useModalActions} from '../../utils/modals'
import {NO_USER_PHOTO} from '../../utils/remote-content'
import {useServicePromo} from './use-service-promo'

const defaultLOptions: LocaleOptions = {caseAction: 'capitalize'}

/* eslint-disable max-lines-per-function */
export const ServiceSummarySection: FC<{
  setIsLoading: (arg: boolean) => void
  setWholePrice?: (arg?: number) => void
  // The summary page has no price block; it takes the code only when there is no payment step.
  withPromo?: boolean
  // eslint-disable-next-line max-lines-per-function
}> = ({setIsLoading, setWholePrice, withPromo = false}) => {
  const {l, language} = useLocale()
  const luxon = useLuxon()
  const params = useParams<{venueId: string}>()
  const {openModal} = useModalActions()
  const {data: venueData} = useQuery(
    VenueQueries.getVenueQuery({
      params: {id: params.venueId},
    }),
  )
  const {values, setFieldValue} = useFormikContext<CreateServiceBookingFormType>()
  const queryData: CalculateBookingPriceQueryParams = useMemo(
    () => ({
      options: [
        {
          date: values.date,
          end: values.time[1] || 0,
          start: values.time[0] || 0,
          venueId: venueData?.uid,
          areaId: values.area?.uid,
          sportId: values.serviceId || '',
          userId: auth.currentUser?.uid,
          coverEquipment: values.coverEquipment,
          useForParticipants: values.useForParticipants,
          timeSlot: values.area?.timeSlot || values.teacher?.timeSlot,
          bookingType: values.entity === 'teacher' ? 'TEACHER' : 'SPORT',
          teacherId: !values.isJustTheSpace ? values.teacher?.uid : undefined,
          duration: getDuration({
            start: values.time[0],
            end: values.time[1],
            isNoDuration: values.service?.pricePeriod === 'NO_DURATION',
          }),
          participants: values.participants.map((el) => {
            const elToStave = {...el}
            delete elToStave.membership
            delete elToStave.membershipActivity
            return elToStave
          }),
        },
      ],
      promoCode: values.promoCode || undefined,
    }),
    [venueData, values, auth.currentUser?.uid],
  )
  const {data, error, isFetching, refetch, dataUpdatedAt} = useQuery(
    BookingQueries.calculateBookingPriceQuery({
      enabled: !!values.participants,
      query: queryData,
      refetchOnWindowFocus: false,
    }),
  )
  useEffect(() => {
    refetch()
  }, [queryData])
  const {getServiceConfigByServiceType} = useServiceOptions([values.service?.serviceType])
  const promo = useServicePromo({
    data,
    isFetching,
    quotedAt: dataUpdatedAt,
    serviceId: values.serviceId,
    enabled: withPromo,
  })
  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching, setIsLoading])
  useEffect(() => {
    setWholePrice && setWholePrice(data?.[0].price)
  }, [isFetching, data, setWholePrice])
  const {bookingDateTime, bookingDuration} = useMemo<{
    bookingDateTime: DateTime
    bookingDuration: string
  }>(() => {
    if (!values.time[0] || !values.time[1]) {
      // VALIDATE FORM AND NAVIGATE
      return {bookingDateTime: luxon.now(), bookingDuration: '-'}
    }
    return {
      bookingDateTime: luxon.fromMillis(values.time[0]),
      bookingDuration:
        values.service?.pricePeriod === 'NO_DURATION'
          ? l('noDuration')
          : `${luxon.fromMillis(values.time[1]).diff(luxon.fromMillis(values.time[0]), 'minutes').toObject().minutes
          } ${l('min')}`,
    }
  }, [l, luxon, values.time])
  const {icon} = useMemo(
    () => getServiceConfigByServiceType(values.service?.serviceType),
    [values.service, getServiceConfigByServiceType],
  )

  const cancelPolicyToUse = values.participants[0]?.membership?.cancelPolicy || values.service?.cancelPolicy
  const cancelPolicyKey = cancelPolicyToUse && FULL_CANCEL_POLICY_OPTIONS_MAP[cancelPolicyToUse]?.description
  const cancelPolicyDescription = cancelPolicyKey ? l(cancelPolicyKey) : cancelPolicyToUse
  // No ladder step: the venue's own policy decides (the pass's, else the service's), as on the server.
  const venuePolicyId = cancelPolicyToUse
    ? null
    : values.participants[0]?.membership?.venueCancelPolicyId || values.service?.venueCancelPolicyId || null
  const {data: venuePolicy} = usePublicCancelPolicy(venueData?.uid, venuePolicyId)

  const usageData: unknown = useMemo(
    () =>
      data
        ? data[0].participants.reduce(
          (acc, participant) => {
            const used: LimitationCheckResultType | null = participant.limitationCheckResult?.used || null
            if (!used) {
              return acc
            }
            const firstPropName: LimitationCheckResultTypeKey | null = Object.keys(
              used,
            )[0] as LimitationCheckResultTypeKey
            return {
              used: acc.used + (used[firstPropName] || 0),
              amountByType: USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP[firstPropName],
            }
          },
          {
            used: 0,
            amountByType: USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP.entries,
          },
        )
        : null,
    [data],
  )

  const playspaceCard = useMemo(() => {
    if (isNilOrEmpty(values.area)) {
      return null
    }
    let subTextElements: unknown[] = []
    if (isNotNilAndNotEmpty(values.area.areaSize)) {
      subTextElements = [values.area.areaSize]
    }
    if (isNotNilAndNotEmpty(values.area.surfaceType)) {
      subTextElements = [...subTextElements, l(values.area.surfaceType, {caseAction: 'capitalize'})]
    }
    return (
      <PlayspaceCard
        styleType='square'
        image={
          <Box
            backgroundSize='cover'
            height='48px'
            width='72px'
            background={`url(${values.area.image}) no-repeat center`}
          />
        }
        name={values.area.name}
        subTextElements={subTextElements as TextType[]}
      />
    )
  }, [values, language])

  const openParticipantsModal = useCallback(() => {
    openModal({
      title: l('participants'),
      footerConfig: {
        disabled: true,
      },
      component: (
        <ParticipantsList
          participants={values.participants}
          displayEquipmentCount={true}
        />
      ),
    })
  }, [values.participants, openModal])

  return (
    <Box>
      <AddonQuoteProblem error={error} />
      <Chip
        text={values.service?.name}
        icon={icon}
        styleType={!values.isJustTheSpace ? 'service' : 'justTheSpace'}
      />
      <TitledSection title={l('booking_information', defaultLOptions)}>
        <LabeledValueRow label={l('dateAndTime')}>
          {bookingDateTime.toUTC().toFormat('HH:mm, MMM dd, yyyy')}
        </LabeledValueRow>
        <LabeledValueRow label={l('duration')}>{bookingDuration}</LabeledValueRow>
        <LabeledValueRow label={l('participants')}>
          <Flex
            cursor='pointer'
            onClick={openParticipantsModal}
          >
            {`${values.participants.length} ${l('participants')}`}
          </Flex>
        </LabeledValueRow>
      </TitledSection>
      <TitledSection title={l('about', defaultLOptions)}>
        {values.area && <LabeledSection label={l('playSpace', defaultLOptions)}>{playspaceCard}</LabeledSection>}
        {values.teacher && (
          <LabeledSection label={l('instructors')}>
            <InstructorCard
              name={values.teacher.name}
              image={
                <Box
                  width='48px'
                  height='48px'
                  backgroundSize='cover'
                  background={`url(${values.teacher.profileImage.secure_url || NO_USER_PHOTO}) no-repeat center`}
                />
              }
            />
            {
              (values.service?.showInstructorDescription && values.teacher.description)
              && (
                <TextWithShowMore text={values.teacher.description} />
              )
            }
          </LabeledSection>
        )}
      </TitledSection>
      {values.participants[0]?.membership && (
        <TitledSection title={l('subscription')}>
          <SubscriptionCard
            usage={usageData as BookingUsageDataType}
            styleType='summary'
            isLoading={isFetching}
            name={values.participants[0].membership.name}
            imageUrl={values.participants[0].membership.logo?.secure_url || ''}
          />
        </TitledSection>
      )}
      <TitledSection title={l('extra_details', defaultLOptions)}>
        <LabeledSection label={l('description')}>
          <TextWithShowMore text={values.service?.description ?? '-'} />
        </LabeledSection>
        <LabeledSection label={l('additionalInformation')}>
          <TextWithShowMore text={values.service?.arrivalInstructions ?? '-'} />
        </LabeledSection>
        {venuePolicy ? (
          <Box pt={'8px'}>
            <LabeledSection label={l('cancelPolicy')}>
              <VenueCancelPolicyInfo
                policy={venuePolicy}
                startMs={values.time[0] ?? null}
              />
            </LabeledSection>
          </Box>
        ) : (
          cancelPolicyDescription && (
            <Box pt={'8px'}>
              <LabeledSection label={l('cancelPolicy')}>{cancelPolicyDescription}</LabeledSection>
            </Box>
          )
        )}
        <Box>
          <Flex
            py={theme.space.multiplier0_5x}
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <P fontSize={'tag'}>{l('specialRequest')}</P>
            <Box>
              <CheckBox id={'hasSpecialRequests'} />
            </Box>
          </Flex>
          {values.hasSpecialRequests && (
            <Box pt={'8px'}>
              <TextArea id={'specialRequest'} detached />
            </Box>
          )}
        </Box>
      </TitledSection>
      {withPromo && (
        <>
          <PromoCodeField
            {...promo.fieldProps}
            venueId={venueData?.uid}
            currency={venueData?.currency}
          />
          {promo.pricingPromo && (
            <PriceRow
              bold
              accent
              isNegative
              price={promo.pricingPromo.amount}
              currency={venueData?.currency ?? ''}
              text={l('promo_line', {code: promo.pricingPromo.code})}
            />
          )}
        </>
      )}
    </Box>
  )
}

export const ServicePricingAndPaymentsSection: FC<{
  setIsLoading: (arg: boolean) => void
  setWholePrice?: (arg?: number) => void
}> = ({setIsLoading, setWholePrice}) => {
  const {l} = useLocale()
  const params = useParams<{venueId: string}>()
  const {data: venueData} = useQuery(
    VenueQueries.getVenueQuery({
      params: {id: params.venueId},
    }),
  )
  const {isLoading} = useBookingLoader()
  const {setFieldValue, values} = useFormikContext<CreateServiceBookingFormType>()
  const isSplitPayment = values.splitPayment === true
  const queryData: CalculateBookingPriceQueryParams = useMemo(
    () => ({
      options: [
        {
          date: values.date,
          end: values.time[1] || 0,
          start: values.time[0] || 0,
          venueId: venueData?.uid,
          areaId: values.area?.uid,
          sportId: values.serviceId || '',
          userId: auth.currentUser?.uid,
          splitPayment: values.splitPayment,
          coverEquipment: values.coverEquipment,
          useForParticipants: values.useForParticipants,
          timeSlot: values.area?.timeSlot || values.teacher?.timeSlot,
          bookingType: values.entity === 'teacher' ? 'TEACHER' : 'SPORT',
          teacherId: !values.isJustTheSpace ? values.teacher?.uid : undefined,
          duration: getDuration({
            start: values.time[0],
            end: values.time[1],
            isNoDuration: values.service?.pricePeriod === 'NO_DURATION',
          }),
          participants: values.participants.map((el) => {
            const elToStave = {...el}
            delete elToStave.membership
            delete elToStave.membershipActivity
            return elToStave
          }),
        },
      ],
      // No promo on a split-payment booking (test plan C16).
      promoCode: isSplitPayment ? undefined : values.promoCode || undefined,
    }),
    [venueData, values, auth.currentUser?.uid, isSplitPayment],
  )
  const {data, error, isFetching, refetch, dataUpdatedAt} = useQuery(
    BookingQueries.calculateBookingPriceQuery({
      enabled: !!values.participants,
      query: queryData,
      refetchOnWindowFocus: false,
    }),
  )
  useEffect(() => {
    refetch()
  }, [queryData, values.splitPayment])
  useEffect(() => {
    if (isSplitPayment && (values.promoCode || values.promoRejection)) {
      void setFieldValue('promoCode', null)
      void setFieldValue('promoRejection', null)
    }
  }, [isSplitPayment])
  const {getServiceConfigByServiceType} = useServiceOptions([values.service?.serviceType])
  const promo = useServicePromo({data, isFetching, quotedAt: dataUpdatedAt, serviceId: values.serviceId})
  const wholeDiscount = useMemo(() => (data ? data.reduce((acc, el) => acc + Number(el.discount), 0) : 0), [data])
  const initialDiscount = useMemo(() => (data ? data.reduce((acc, el) => acc + Number(el.initialDiscount), 0) : 0), [data])
  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching, setIsLoading])
  useEffect(() => {
    setWholePrice && setWholePrice(data?.[0].price)
  }, [isFetching, data, setWholePrice])

  const bookingPriceData = useMemo(() => {
    if (!data) {
      return []
    }
    const participantsData = data.reduce<PricingElementType[]>((acc, el: CalculatedBookingPriceObject) => {
      const timeArr: string[] = []
      if (el.duration.hours) {
        timeArr.push(l('hours_short', {value: el.duration.hours}))
      }
      if (el.duration.minutes) {
        timeArr.push(l('minutes_short', {value: el.duration.minutes}))
      }
      const priceAndDiscount = el.participants.reduce(
        (acc2, participant) => ({
          price: acc2.price + participant.activityPrice,
          discount: acc2.discount + participant.discount,
        }),
        {
          price: 0,
          discount: 0,
        }
      )
      if (values.splitPayment === true) {
        el.participants.forEach((participant, index) => {
          const participantLine = {
            discount: participant.discount,
            count: 0,
            id: `participants.${index}.toPay`,
            withCheck: true,
            onCheckChange: () => {
              setFieldValue(`participants.${index}.toPay`, !participant.toPay)
            },
            subtext: <Flex>
              {participant.name}
              {
                participant.membershipName
                && <Divider styleType='dot' />
              }
              {
                participant.membershipName
                  ? (<Flex maxWidth={['200px', '400px']}>
                    <IconWithText
                      icon='crownAction'
                      text={participant.membershipName}
                      settings={{icon: {color: '#FF9E00'}, text: {color: '#FF9E00'}}}
                    />
                  </Flex>) : null
              }
            </Flex>,
            checked: participant.toPay,
            price: participant.activityPrice,
            text: <Flex>{index+1}/{el.participants.length}<Icon icon='user' /> {l('service_price_element', {time: timeArr.join(' '), service: values.service?.name})}</Flex>,
          }
          const equipmentsLine = getParticipantsEquipment([participant], true, true)
          acc.push(...[participantLine, ...equipmentsLine])
        })
      } else {
        const equipments = getParticipantsEquipment(data.reduce<Participant[]>((partAcc, option) => [...partAcc, ...option.participants], []), true)
        acc.push(...[{
          count: el.numberOfPeople,
          price: priceAndDiscount.price,
          discount: priceAndDiscount.discount,
          text: l('service_price_element', {time: timeArr.join(' '), service: values.service?.name}),
        }, ...equipments])
      }
      return acc
    }, [])
    return [
      ...participantsData,
      // ...equipments,
    ]
  }, [data, l, values]) // eslint-disable-line
  const totalPrice = useMemo(
    () =>
      data
        ? data.reduce((acc, el) => acc + Number(el.totalPrice), 0)
        : 0,
    [data],
  )

  return (
    <Box>
      <AddonQuoteProblem error={error} />
      {!isSplitPayment && (
        <PromoCodeField
          {...promo.fieldProps}
          venueId={venueData?.uid}
          currency={venueData?.currency}
        />
      )}
      <PricingDetails
        isLoading={isFetching}
        discount={wholeDiscount}
        isProcessing={isLoading}
        values={bookingPriceData}
        withDividerBetweenItems={true}
        initialDiscount={initialDiscount}
        withoutCurrency={false}
        currency={venueData?.currency ?? ''}
        total={totalPrice}
        predefinedLength={values.splitPayment === true ? values.participants.length : 1}
        promo={isSplitPayment ? null : promo.pricingPromo}
      />
    </Box>
  )
}
